import { useEffect, useRef, useState } from 'react';
import { NOTIFY_LINE, summarizeRule } from './mailRuleSummary';
import MailNotifyErrorNotice from './MailNotifyErrorNotice';
import { FALLBACK, describeError } from './mailNotifyStatus';
import './MailRuleSection.css';

/** 每人規則上限（AGUI-1113 由 10 條降為 3 條）；⛔ 後端 mygenieap 另有同一個上限，兩邊要一致 */
export const MAX_RULES = 3;

/** 垃圾桶圖示，與 ScheduleView 的 ActionIcon type="delete" 相同 */
function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

const STATE_OPTIONS = [
  { value: true, label: '啟用' },
  { value: false, label: '停用' },
];

/**
 * 規則狀態選單（AGUI-1258，10-19 使用者指定樣式）：灰色膠囊按鈕，點開是白色圓角選單，選中的項目右側打勾。
 * 不用原生 select：原生選單的外觀各平台不同、改不了。點外面或 Esc 收起。
 */
function RuleStateMenu({ ruleName, enabled, disabled, onSelect }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // 送出中被鎖住時一併收起
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  return (
    <div className={`mail-rule-state${open ? ' mail-rule-state--open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className={`mail-rule-state__trigger${enabled ? '' : ' mail-rule-state__trigger--off'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${ruleName} 的狀態：${enabled ? '啟用' : '停用'}`}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        {enabled ? '啟用' : '停用'}
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2.5 4.25L6 7.75l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul className="mail-rule-state__menu" role="listbox" aria-label={`${ruleName} 的狀態`}>
          {STATE_OPTIONS.map((opt) => {
            const selected = opt.value === enabled;
            return (
              <li key={opt.label} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className="mail-rule-state__option"
                  onClick={() => {
                    setOpen(false);
                    onSelect(opt.value);
                  }}
                >
                  {opt.label}
                  {selected && (
                    <svg className="mail-rule-state__check" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19l12-12-1.41-1.41z" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * 我的規則（AGUI-1113）。兩欄版面的左欄，接在設定卡下方。
 *
 * ⛔ 本區塊<b>永遠掛載</b>，即時通知關著也要看得到（A2 乙）：對話入口隨時可以建規則，
 * 建了卻在頁面上找不到，使用者只會以為沒建成功。
 *
 * 規則卡預設展開複述（10-06 使用者裁決；上限只有 3 條，全部攤開也塞不滿一欄）；
 * 點開關左側整塊可收合／展開。
 *
 * 新增表單不在這裡：它開在右欄，由 MailNotificationViewV2 掌管，本元件只發出 {@code onAdd}。
 *
 * 規則與動作都由頁面的 useMailNotify 提供（SA v2.0 C08～C11），本元件不自己讀 API。
 * AGUI-1246 舊架構下線後，原本自己讀 API 的舊模式（訂閱狀態、未生效徽章、reloadKey）一併移除。
 *
 * @param {Array} rules 目前的規則
 * @param {string} [state] 通知狀態；只有 ACTIVE 才說「將收到通知」，免得與狀態卡矛盾（C09）
 * @param {boolean} [busy] 頁面處理中：狀態下拉與「＋」一起鎖住
 * @param {boolean} [isMobile] 手機版：錯誤條不給「前往設定」
 * @param {Function} [onAdd] 按「＋」時呼叫，請外層打開新增表單
 * @param {boolean} [adding] 新增表單是否開著；開著時停用「＋」
 * @param {Function} onToggle `(rule) => Promise<{ ok, error?, timedOut? }>`
 * @param {Function} onRemove `(rule, onPending) => Promise<{ ok, error?, timedOut? }>`；onPending 在收到 202 時呼叫
 * @param {Function} [onReload] 刪除失敗關框後重讀清單與狀態（不顯示「同步中」，規則區不收起）
 */
export default function MailRuleSection({
  rules = [], state, busy = false, isMobile = false, onAdd, adding = false, onToggle, onRemove, onReload,
}) {
  const [error, setError] = useState(null);
  // 規則卡預設展開（10-06 使用者裁決，取代 A10 的預設收合），所以記的是「收起來的」；可以各自收合，互不影響
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // 切換送出後、還沒有結果時下拉先停在新值（C10：202 時停在新值但 disabled）
  const [optimistic, setOptimistic] = useState(null);
  // AGUI-1258：送出期間<b>所有</b>規則的下拉都鎖住，完成才恢復。用 ref 同步擋，連續兩次選擇可能落在同一次重新渲染之前；
  // UAT 曾因連續切換打出 409 OPERATION_IN_PROGRESS／VERSION_CONFLICT
  const togglingRef = useRef(false);

  const handleToggle = async (rule, nextEnabled) => {
    if (togglingRef.current || nextEnabled === rule.enabled) return;
    togglingRef.current = true;
    setBusyId(rule.ruleId);
    setError(null);
    setOptimistic({ ruleId: rule.ruleId, enabled: nextEnabled });
    const result = await onToggle(rule);
    // 成功時清單已由頁面重讀成新狀態；失敗時拿掉暫時狀態，下拉回到原值
    setOptimistic(null);
    setBusyId(null);
    togglingRef.current = false;
    if (!result.ok && result.error) setError(result.error);
  };

  // AGUI-1269（10-21 改）：確認框留著，「刪除」變「刪除中...」，等刪除與重讀清單（含 202 輪詢）都完成才關，
  // 規則卡與確認框同時消失；原本按下就關、要等 1～2 秒卡片才不見，會以為沒按到。
  // 送出期間「取消」、點外面都不關，這張卡的垃圾桶與所有狀態選單鎖住，防止重複送出。
  // 失敗時一律關框並重讀（10-21 使用者裁決，取代 C 方案的「可重試的留在框內」）：留在框內重按，
  // 只有網路瞬斷與重讀成功的版本衝突會成功；設定異常、上一個操作還在處理、重讀失敗的版本衝突重按都只會再失敗。
  // 關框後錯誤顯示在面板頂端，清單與狀態重讀成最新，使用者看清現況再決定要不要重刪
  const deletingId = confirmingDelete && busyId === confirmingDelete.ruleId ? busyId : null;
  const closeDeleteDialog = () => {
    if (!deletingId) setConfirmingDelete(null);
  };
  const handleDelete = async (rule) => {
    if (busyId !== null) return;
    setBusyId(rule.ruleId);
    setError(null);
    const result = await onRemove(rule);
    setBusyId(null);
    setConfirmingDelete(null);
    if (result.ok) return;
    // 失敗後重讀，這條規則已經不在（例如版本衝突，其實已在別處刪除）：與 404 同樣處理
    const gone = Boolean(result.reloaded) && !result.reloaded.rules?.some((r) => r.ruleId === rule.ruleId);
    if (gone) setError(describeError({ errorCode: 'RULE_NOT_FOUND' }, FALLBACK.delete));
    else if (result.error) setError(result.error);
    // write() 遇到要重讀的錯誤（409、404…）已經重讀過，其餘（網路、503、422、403、輪詢逾時）在這裡補讀
    if (!result.reloaded && onReload) onReload();
  };

  const activeCount = rules.filter((r) => r.enabled).length;
  // 規則區本身不標「未生效」，現況一律由狀態卡講（SA §2.3）；
  // 有啟用中的規則、但狀態不是已啟用時（處理中、已暫停…），不講「將收到通知」，免得與狀態卡矛盾（C09）
  const showActiveStatus = state === 'ACTIVE';
  const addDisabled = rules.length >= MAX_RULES || adding || busy;

  return (
    // AGUI-1289：手機版選單與垃圾桶固定在右上角，名稱與條件用滿卡片寬度（樣式見 .mail-rule-section--mobile）
    <div className={`mail-rule-section${isMobile ? ' mail-rule-section--mobile' : ''}`}>
      {/* 刪除確認沿用「建立排程」的 delete-confirm 對話框（App.css），點外面等同取消 */}
      {confirmingDelete && (
        <div className="delete-confirm-overlay" onClick={closeDeleteDialog}>
          <div
            className="delete-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mail-rule-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 講清楚刪掉的後果：使用者常把「刪除規則」當成「先停掉」，那件事有專門的啟用切換，不必刪。
                「？」後換行，電腦與手機都固定兩行（10-19 使用者裁決） */}
            <div id="mail-rule-delete-title" className="delete-confirm-title">
              <span className="mail-rule-section__delete-line">刪除規則「{confirmingDelete.ruleName}」？</span>
              <span className="mail-rule-section__delete-line">刪除後不再依此規則通知，此操作無法復原。</span>
            </div>
            <div className="delete-confirm-buttons">
              <button type="button" className="delete-confirm-btn cancel-btn" disabled={Boolean(deletingId)}
                onClick={closeDeleteDialog}>
                取消
              </button>
              <button
                type="button"
                className="delete-confirm-btn confirm-btn"
                disabled={Boolean(deletingId)}
                onClick={() => handleDelete(confirmingDelete)}
              >
                {deletingId ? '刪除中...' : '刪除'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mail-rule-section__header">
        <span className="mail-rule-section__title">
          我的規則 <span className="mail-rule-section__count">{rules.length} / {MAX_RULES}</span>
        </span>
        {/* 滿 3 條時說明為什麼不能按（SA S-10，10-19 使用者裁決）；停用的按鈕在 Chrome 不顯示 title，所以提示放在外層 */}
        <span className="mail-rule-section__add-wrap" title={rules.length >= MAX_RULES ? `已達 ${MAX_RULES} 條規則上限` : undefined}>
          <button
            type="button"
            className="mail-rule-section__add"
            aria-label="新增規則"
            title={rules.length >= MAX_RULES ? undefined : '新增規則'}
            disabled={addDisabled}
            onClick={() => onAdd && onAdd()}
          >
            ＋
          </button>
        </span>
      </div>
      {/* 規則存在 MyAgent，兩平台讀寫同一份（C08）。10-19 PO 檢視：畫面上不提 MyAgent，原本常駐的共用說明拿掉 */}

      {/* AGUI-1190：依規則狀態講清楚會不會收到通知——沒有規則、全部停用是警示；有啟用的規則是一般說明 */}
      {activeCount === 0 ? (
        <div className="mail-alert-task__notice" role="alert">
          <span className="mail-alert-task__notice-text">
            {rules.length === 0 ? '尚未設定規則，不會收到任何通知。' : '目前沒有啟用中的規則，不會收到任何通知。'}
          </span>
        </div>
      ) : (
        showActiveStatus && (
          <div className="mail-rule-section__status" role="status">
            已啟用 {activeCount} 個規則，符合條件時將收到通知。
          </div>
        )
      )}
      <MailNotifyErrorNotice error={error} isMobile={isMobile} />

      {rules.map((rule) => {
        const expanded = !collapsedIds.has(rule.ruleId);
        const enabled = optimistic?.ruleId === rule.ruleId ? optimistic.enabled : rule.enabled;
        return (
          <div
            key={rule.ruleId}
            className={`mail-rule-section__rule${enabled ? '' : ' mail-rule-section__rule--off'}`}
          >
            {/* 10-07：點擊範圍是整張卡片（CSS ::after 蓋滿卡片），開關與垃圾桶疊在上面、各做各的動作。
                按鈕仍是唯一可及的展開元素，鍵盤與螢幕閱讀器的行為不變 */}
            <button
              type="button"
              className="mail-rule-section__rule-toggle"
              aria-expanded={expanded}
              onClick={() =>
                setCollapsedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(rule.ruleId)) next.delete(rule.ruleId);
                  else next.add(rule.ruleId);
                  return next;
                })
              }
            >
              <span className="mail-rule-section__rule-name">
                {/* AGUI-1270：名稱包一層才能在 flex 裡縮小換行，長名稱不會壓到右側的狀態選單與垃圾桶 */}
                <span className="mail-rule-section__rule-name-text">{rule.ruleName}</span>
                {rule.aiPrompt && <span className="mail-rule-form__ai-badge">AI</span>}
                {/* 10-22 使用者裁決：不再加「已停用」標籤，規則狀態只用右側選單的「啟用／停用」表達，
                    避免同一張卡出現「已停用」與「停用」兩種說法（停用的卡片仍淡化名稱與條件） */}
              </span>

              {/* 「最近已通知的信」已移除（使用者裁決，取代 US-RULE-08）：展開只顯示條件，一個條件一行。
                  放在 button 裡只能用 span */}
              {expanded && (
                <span className="mail-rule-section__rule-summary">
                  {summarizeRule(rule)
                    .filter((line) => line !== NOTIFY_LINE)
                    .map((line) => (
                      <span key={line}>{line}</span>
                    ))}
                </span>
              )}
            </button>

            <div className="mail-rule-section__rule-actions">
              {/* AGUI-1258：開關改為下拉選單（啟用／停用）。可逆，不需二次確認（BR-CHAT-11 的同一個道理）；
                  任何一條規則送出中時全部鎖住 */}
              <RuleStateMenu
                ruleName={rule.ruleName}
                enabled={enabled}
                disabled={busyId !== null || busy}
                onSelect={(next) => handleToggle(rule, next)}
              />

              <button
                type="button"
                className="schedule-icon-btn schedule-icon-btn--danger mail-rule-section__remove"
                aria-label={`刪除 ${rule.ruleName}`}
                title="刪除規則"
                disabled={busyId === rule.ruleId}
                onClick={() => setConfirmingDelete(rule)}
              >
                <TrashIcon />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
